// src/components/dashboard/ExtractionProgress.tsx
//
// Shared "the system is working" cue for the 30-60 s between claim and
// PENDING_APPROVAL (AI extraction + pricing + draft). Used on the inbound card
// (compact) and on the review screen (panel).
//
// Deliberately honest: an indeterminate sweep and a real elapsed timer, not a
// staged "step 2 of 4" bar -- the backend does not report stage progress, so
// any per-step text driven by elapsed time would be invented.
import { useEffect, useState } from 'react';
import { brand, fonts, styles } from '@/lib/tokens';

/** Typical end-to-end extraction time shown to the operator. */
export const EXPECTED_RANGE_LABEL = '30-60 seconds';
/** After this the copy switches to a "taking longer" message. */
const SLOW_AFTER_SECONDS = 90;

type Props = {
  /** Epoch ms when this session started the claim. Omit when unknown (e.g. a
   *  card that was already PROCESSING on page load) -- the timer is hidden
   *  rather than guessed. */
  startedAt?: number;
  clientName?: string;
  variant?: 'compact' | 'panel';
};

function useElapsedSeconds(startedAt?: number): number | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (startedAt === undefined) return;
    const handle = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(handle);
  }, [startedAt]);
  if (startedAt === undefined) return null;
  return Math.max(0, Math.floor((now - startedAt) / 1000));
}

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${String(s).padStart(2, '0')}s` : `${s}s`;
}

export function IndeterminateBar() {
  return (
    <div
      aria-hidden
      style={{
        position: 'relative',
        height: 3,
        width: '100%',
        borderRadius: 2,
        background: brand.accentFaint,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          height: '100%',
          width: '40%',
          borderRadius: 2,
          background: brand.accent,
          animation: 'extractionSweep 1.6s ease-in-out infinite',
        }}
      />
    </div>
  );
}

export function ExtractionProgress({ startedAt, clientName, variant = 'panel' }: Props) {
  const elapsed = useElapsedSeconds(startedAt);
  const slow = elapsed !== null && elapsed >= SLOW_AFTER_SECONDS;

  const headline = slow ? 'Taking longer than usual' : 'AI is reading this email and preparing a quote';
  const detail = slow
    ? 'Still working. If nothing appears soon it will be flagged as failed and you can retry.'
    : `Usually ready in ${EXPECTED_RANGE_LABEL}. You can leave this screen -- you will be notified when it is ready.`;

  if (variant === 'compact') {
    return (
      <div role="status" aria-live="polite" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontFamily: fonts.mono, fontSize: 11, color: brand.accent }}>
          {slow ? 'Taking longer than usual -- still working' : `AI extraction in progress -- usually ${EXPECTED_RANGE_LABEL}`}
          {elapsed !== null && <span style={{ color: brand.monoDim }}> {' · '} {formatElapsed(elapsed)}</span>}
        </div>
        <IndeterminateBar />
      </div>
    );
  }

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 32,
        minWidth: 0,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 420,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 16,
          textAlign: 'center',
        }}
      >
        <span
          className="material-symbols-outlined"
          style={{ fontSize: 28, color: brand.accent, animation: 'spin 1.2s linear infinite' }}
        >
          autorenew
        </span>
        <div style={{ fontFamily: fonts.serif, fontSize: 22, color: brand.onSurface, lineHeight: 1.2 }}>
          {headline}
        </div>
        {clientName && clientName !== 'Loading...' && (
          <div style={{ ...styles.monoLabel, color: brand.accent }}>{clientName}</div>
        )}
        <IndeterminateBar />
        <div style={{ fontFamily: fonts.sans, fontSize: 13, color: brand.textSoft, lineHeight: 1.5 }}>{detail}</div>
        {elapsed !== null && (
          <div style={{ ...styles.monoLabel, color: brand.monoDim }}>ELAPSED {formatElapsed(elapsed)}</div>
        )}
      </div>
    </div>
  );
}
